import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { shiftService, siteService, userService } from '../../services';
import { Shift, ShiftStatus, ShiftFilter, SiteFilter, Site, User } from '../../types';
import { ShiftCard } from '../../components/ShiftCard';
import { USER_ROLES } from '../../constants';
import { toast } from '../../components/Toast';
import styles from './Shifts.module.css';

export const Shifts: React.FC = () => {
  const { user } = useAuth();
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sites, setSites] = useState<Site[]>([]);
  const [employees, setEmployees] = useState<User[]>([]);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingShift, setEditingShift] = useState<Shift | null>(null);

  // Filtri
  const [statusFilter, setStatusFilter] = useState<ShiftStatus | ''>('');
  const [siteFilter, setSiteFilter] = useState<number | ''>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Form state
  const [formData, setFormData] = useState({
    siteId: user?.siteId || 0,
    employeeId: '' as number | '',
    role: '',
    startTime: '',
    endTime: '',
    notes: '',
  });

  const loadShifts = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const filter: ShiftFilter = { page, pageSize: 10 };

      if (statusFilter !== '') {
        filter.status = statusFilter;
      }

      if (siteFilter !== '') {
        filter.siteId = siteFilter;
      }

      // Employee vede solo i propri turni
      if (user?.role === USER_ROLES.EMPLOYEE) {
        filter.employeeId = user.id;
      }

      const response = await shiftService.getShifts(filter);
      setShifts(response.data);
      setTotalPages(response.totalPages);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Errore nel caricamento dei turni';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, siteFilter, user]);

  useEffect(() => {
    loadShifts();
  }, [loadShifts]);

  // Carica lista sedi disponibili
  useEffect(() => {
    const loadSites = async () => {
      try {
        const filter: SiteFilter = {
          isActive: true,
          pageSize: 100,
        };

        if (user?.role === USER_ROLES.OWNER && user?.companyId) {
          filter.companyId = user.companyId;
        }

        const response = await siteService.getSites(filter);
        setSites(response.data);
      } catch (err) {
        console.error('Errore nel caricamento delle sedi:', err);
      }
    };

    if (user && user.role <= USER_ROLES.OWNER) {
      loadSites();
    }
  }, [user]);

  // Carica dipendenti per il form
  const loadEmployees = useCallback(async (siteId?: number) => {
    try {
      const filter: Record<string, number | string | boolean> = {
        role: USER_ROLES.EMPLOYEE,
        pageSize: 100,
      };
      if (siteId) {
        filter.siteId = siteId;
      }
      const response = await userService.getUsers(filter);
      setEmployees(response.data);
    } catch (err) {
      console.error('Errore nel caricamento dipendenti:', err);
    }
  }, []);

  const openCreateModal = useCallback(() => {
    setEditingShift(null);
    setFormData({
      siteId: user?.siteId || (sites.length === 1 ? sites[0].id : 0),
      employeeId: '',
      role: '',
      startTime: '',
      endTime: '',
      notes: '',
    });
    setShowModal(true);
    loadEmployees(user?.siteId || undefined);
  }, [user, sites, loadEmployees]);

  const openEditModal = useCallback((shift: Shift) => {
    setEditingShift(shift);
    setFormData({
      siteId: shift.siteId,
      employeeId: shift.employeeId || '',
      role: shift.role,
      startTime: shift.startTime.slice(0, 16), // format for datetime-local
      endTime: shift.endTime.slice(0, 16),
      notes: shift.notes || '',
    });
    setShowModal(true);
    loadEmployees(shift.siteId);
  }, [loadEmployees]);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setEditingShift(null);
    setError('');
  }, []);

  const handleSiteChange = useCallback((siteId: number) => {
    setFormData(prev => ({ ...prev, siteId, employeeId: '' }));
    if (siteId) {
      loadEmployees(siteId);
    }
  }, [loadEmployees]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      if (editingShift) {
        await shiftService.updateShift(editingShift.id, {
          employeeId: formData.employeeId || undefined,
          role: formData.role || undefined,
          startTime: formData.startTime || undefined,
          endTime: formData.endTime || undefined,
          notes: formData.notes || undefined,
        });
        toast.success('Turno aggiornato con successo!');
      } else {
        await shiftService.createShift({
          siteId: formData.siteId,
          employeeId: formData.employeeId || undefined,
          role: formData.role,
          startTime: formData.startTime,
          endTime: formData.endTime,
          notes: formData.notes || undefined,
        });
        toast.success('Turno creato con successo!');
      }
      closeModal();
      loadShifts();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Errore nel salvataggio del turno';
      setError(message);
    }
  }, [editingShift, formData, closeModal, loadShifts]);

  const handleClockIn = async (id: number) => {
    try {
      await shiftService.clockIn(id);
      toast.success('Clock in effettuato con successo!');
      loadShifts();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Errore nel clock in';
      toast.error(message);
    }
  };

  const handleClockOut = async (id: number) => {
    try {
      await shiftService.clockOut(id);
      toast.success('Clock out effettuato con successo!');
      loadShifts();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Errore nel clock out';
      toast.error(message);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Sei sicuro di voler cancellare questo turno?')) {
      return;
    }

    try {
      await shiftService.deleteShift(id);
      toast.success('Turno cancellato con successo');
      loadShifts();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Errore nella cancellazione del turno';
      toast.error(message);
    }
  };

  const canManageShifts = user && user.role <= USER_ROLES.MANAGER;
  const isManager = user?.role === USER_ROLES.MANAGER;

  return (
    <div className={styles.shifts}>
      <div className={styles.header}>
        <h1>Gestione Turni</h1>
        {canManageShifts && (
          <button className={styles.createButton} onClick={openCreateModal}>
            + Crea Turno
          </button>
        )}
      </div>

      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <label htmlFor="status">Stato:</label>
          <select
            id="status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value === '' ? '' : parseInt(e.target.value) as ShiftStatus);
              setPage(1);
            }}
            className={styles.select}
          >
            <option value="">Tutti</option>
            <option value={ShiftStatus.Scheduled}>Programmati</option>
            <option value={ShiftStatus.InProgress}>In Corso</option>
            <option value={ShiftStatus.Completed}>Completati</option>
            <option value={ShiftStatus.Cancelled}>Cancellati</option>
          </select>
        </div>

        {user && user.role <= USER_ROLES.OWNER && (
          <div className={styles.filterGroup}>
            <label htmlFor="site">Sede:</label>
            <select
              id="site"
              value={siteFilter}
              onChange={(e) => {
                setSiteFilter(e.target.value ? parseInt(e.target.value) : '');
                setPage(1);
              }}
              className={styles.select}
            >
              <option value="">Tutte le sedi</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {error && !showModal && (
        <div className={styles.error}>{error}</div>
      )}

      {loading ? (
        <div className={styles.loading}>Caricamento turni...</div>
      ) : shifts.length === 0 ? (
        <div className={styles.empty}>
          <p>Nessun turno trovato</p>
        </div>
      ) : (
        <>
          <div className={styles.grid}>
            {shifts.map((shift) => (
              <ShiftCard
                key={shift.id}
                shift={shift}
                onClockIn={user?.role === USER_ROLES.EMPLOYEE ? handleClockIn : undefined}
                onClockOut={user?.role === USER_ROLES.EMPLOYEE ? handleClockOut : undefined}
                onEdit={canManageShifts ? (id: number) => {
                  const s = shifts.find(sh => sh.id === id);
                  if (s) openEditModal(s);
                } : undefined}
                onDelete={canManageShifts ? handleDelete : undefined}
                showActions={true}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className={styles.pageButton}
              >
                Precedente
              </button>
              <span className={styles.pageInfo}>
                Pagina {page} di {totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                className={styles.pageButton}
              >
                Successiva
              </button>
            </div>
          )}
        </>
      )}

      {/* Modal Crea/Modifica Turno */}
      {showModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{editingShift ? 'Modifica Turno' : 'Nuovo Turno'}</h2>
              <button className={styles.closeButton} onClick={closeModal}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
              {/* Sede */}
              <div className={styles.formGroup}>
                <label htmlFor="shiftSiteId">Sede *</label>
                <select
                  id="shiftSiteId"
                  value={formData.siteId}
                  onChange={(e) => handleSiteChange(parseInt(e.target.value))}
                  className={styles.select}
                  required
                  disabled={isManager}
                >
                  <option value={0}>-- Seleziona Sede --</option>
                  {isManager && user?.siteId ? (
                    <option value={user.siteId}>{user.siteName}</option>
                  ) : (
                    sites.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.name} - {site.city}
                      </option>
                    ))
                  )}
                </select>
              </div>

              {/* Dipendente */}
              <div className={styles.formGroup}>
                <label htmlFor="shiftEmployeeId">Dipendente</label>
                <select
                  id="shiftEmployeeId"
                  value={formData.employeeId}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    employeeId: e.target.value ? parseInt(e.target.value) : ''
                  }))}
                  className={styles.select}
                >
                  <option value="">-- Non assegnato --</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.firstName} {emp.lastName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Ruolo */}
              <div className={styles.formGroup}>
                <label htmlFor="shiftRole">Ruolo *</label>
                <input
                  type="text"
                  id="shiftRole"
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                  className={styles.input}
                  placeholder="es. Cameriere, Barista, Cuoco..."
                  required
                />
              </div>

              {/* Data/Ora */}
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="shiftStart">Inizio *</label>
                  <input
                    type="datetime-local"
                    id="shiftStart"
                    value={formData.startTime}
                    onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
                    className={styles.input}
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="shiftEnd">Fine *</label>
                  <input
                    type="datetime-local"
                    id="shiftEnd"
                    value={formData.endTime}
                    onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
                    className={styles.input}
                    required
                  />
                </div>
              </div>

              {/* Note */}
              <div className={styles.formGroup}>
                <label htmlFor="shiftNotes">Note</label>
                <textarea
                  id="shiftNotes"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className={styles.textarea}
                  rows={3}
                  placeholder="Note opzionali..."
                />
              </div>

              {error && <div className={styles.error}>{error}</div>}

              <div className={styles.formActions}>
                <button type="button" className={styles.cancelButton} onClick={closeModal}>
                  Annulla
                </button>
                <button type="submit" className={styles.submitButton}>
                  {editingShift ? 'Salva Modifiche' : 'Crea Turno'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

Shifts.displayName = 'Shifts';
